import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Clock, Sparkles } from 'lucide-react';
import { api } from '../api/client';
import { useSubscriptionSelection } from '../context/SubscriptionContext';
import type { SubscriptionProduct, BillingCycle } from '../api/types';

const Subscriptions: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [themes, setThemes] = useState<Awaited<ReturnType<typeof api.subscriptionThemes>> | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionProduct[]>([]);
  const [selectedCycleByProduct, setSelectedCycleByProduct] = useState<Record<string, BillingCycle>>({});
  const { setSelection } = useSubscriptionSelection();
  const navigate = useNavigate();

  useEffect(() => {
    api.plans().then(setSubscriptions).catch(console.error);
    api.subscriptionThemes().then(setThemes).catch(console.error);

    const calculateTimeLeft = () => {
    const now = new Date();
    
    // Get current date in Central Time
    const centralTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
    
    // Determine target date (25th of current or next month at midnight CT)
    let targetDate = new Date(centralTime.getFullYear(), centralTime.getMonth(), 25, 0, 0, 0);
    
    // If we're past the 25th, target next month's 25th
    if (centralTime.getDate() >= 25) {
      targetDate = new Date(centralTime.getFullYear(), centralTime.getMonth() + 1, 25, 0, 0, 0);
    }
    
    // Convert target to UTC for calculation
    const targetUTC = new Date(targetDate.toLocaleString("en-US", { timeZone: "America/Chicago" }));
    const difference = targetUTC.getTime() - centralTime.getTime();
    
    if (difference > 0) {
      setTimeLeft({
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
      });
    }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, SubscriptionProduct[]>();
    subscriptions.forEach(p => {
      const key = p.productName;
      const arr = map.get(key) || [];
      arr.push(p);
      map.set(key, arr);
    });
    return map;
  }, [subscriptions]);

  const getMonthlyPlan = (plans: SubscriptionProduct[]) => plans.find(p => p.subscriptionTerm === 'Monthly');
  const getAnnualPlan = (plans: SubscriptionProduct[]) => plans.find(p => p.subscriptionTerm === 'Annual');

  const benefits = [
	  'Free Antique Flower packet on sign-up',
    'Get a fresh theme every month on the 25th',
    'Monthly packets exclusive for 1 month',
    'Save over 25% compared to buying individually',
    'Additional 50% savings with an annual plan',
  ];

  const currentTheme = themes?.current;
  const nextTheme = themes?.next;

  const computeDisplayPrice = (plans: SubscriptionProduct[], cycle: BillingCycle) => {
    const monthly = getMonthlyPlan(plans);
    if (!monthly) return undefined;
    if (cycle === 'Monthly') return monthly.price;
    return monthly.price * 12 * 0.5; // annual at 50% off yearly total
  };

  const goToSubscribe = (productName: string, plans: SubscriptionProduct[]) => {
    const cycle = selectedCycleByProduct[productName] || 'Monthly';
    const monthly = getMonthlyPlan(plans);
    const annual = getAnnualPlan(plans);

    const optionsByTerm = plans.reduce((acc, s) => {
      acc[s.subscriptionTerm] = { planVariationId: s.squarePlanVariationId, price: s.price, locationId: s.locationId, productId: s.productId };
      return acc;
    }, {} as Record<string, { planVariationId: string; price: number; locationId?: string; productId: string }>);

    const selectedPlanVariationId = cycle === 'Monthly' ? monthly?.squarePlanVariationId : annual?.squarePlanVariationId ?? monthly?.squarePlanVariationId;
    const selectedPrice = computeDisplayPrice(plans, cycle) ?? monthly?.price ?? 0;
    const anyPlan = monthly ?? annual ?? plans[0];

    setSelection({
      planId: selectedPlanVariationId ?? anyPlan.squarePlanVariationId,
      interval: cycle,
      locationId: anyPlan.locationId,
      productId: anyPlan.productId,
      productName,
      price: selectedPrice,
      availableTerms: Array.from(new Set(plans.map(p => p.subscriptionTerm))).sort(),
      optionsByTerm,
    });
    navigate('/subscribe');
  };

  return (
    <div className="min-h-screen">
      <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 md:p-8 py-3 sticky top-20 z-[40]">
      <div className="max-w-[500px] mx-auto">
        <div className="flex items-center gap-2 mb-2 md:mb-4">
          <Clock className="w-5 md:w-6 h-5 md:h-6 text-[#C9A962]" />
          <p className="font-sans text-[#3D3D3D]/90 font-medium text-sm md:text-lg">
            Sign up by {themes?.cutoffMonthName ?? 'the 25th'} 24th to receive {currentTheme?.monthName ?? 'next month'}'s package!
          </p>
        </div>
        <div className="grid grid-cols-4 gap-3 md:gap-4">
          {[
            { value: timeLeft.days, label: 'Days' },
            { value: timeLeft.hours, label: 'Hours' },
            { value: timeLeft.minutes, label: 'Minutes' },
            { value: timeLeft.seconds, label: 'Seconds' }
          ].map((item, index) => (
            <div key={index} className="text-center">
              <div className="bg-[#3D3D3D]/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-2 mb-2">
                <span className="font-serif text-3xl font-bold text-[#3D3D3D]">
                  {String(item.value).padStart(2, '0')}
                </span>
              </div>
              <span className="font-sans text-sm md:text-md text-[#3D3D3D]/60">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
      </div>
      <section className="bg-gradient-to-br from-[#C9A962]/20 to-[#FDF8F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A962]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#C9A962]" />
              <span className="font-sans text-sm text-[#C9A962] font-medium">Membership</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[#3D3D3D] mb-4">Choose Your Journey</h1>
            <p className="font-sans text-lg text-[#3D3D3D]/70 max-w-2xl mx-auto">Join our community and receive beautiful, hand-designed pages every month</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16 justify-items-center">
          {Array.from(grouped.entries()).map(([productName, plans], index, arr) => {
            const cycle = selectedCycleByProduct[productName] || 'Monthly';
            const displayPrice = computeDisplayPrice(plans, cycle);
            const singleItem = arr.length === 1;
            return (
              <motion.div key={productName} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} className={singleItem ? 'md:col-start-2' : ''}>
                <div className="relative bg-white rounded-3xl p-8 shadow-sm transition-all duration-300 w-full max-w-md">
                  <h3 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-2 text-center">{productName}</h3>
                  {typeof displayPrice === 'number' && (
                    <div className="flex items-baseline gap-1 mb-4 justify-center">
                      <span className="font-serif text-4xl font-semibold text-[#3D3D3D]">${displayPrice.toFixed(2)}</span>
                      <span className="font-sans text-[#3D3D3D]/60">{cycle === 'Monthly' ? '/month' : '/year'}</span>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${cycle === 'Monthly' ? 'bg-[#3D3D3D] text-white border-[#3D3D3D]' : 'bg-white text-[#3D3D3D] border-gray-300'}`}
                      onClick={() => setSelectedCycleByProduct(prev => ({ ...prev, [productName]: 'Monthly' }))}
                      aria-pressed={cycle === 'Monthly' ? 'true' : 'false'}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${cycle === 'Annual' ? 'bg-[#3D3D3D] text-white border-[#3D3D3D]' : 'bg-white text-[#3D3D3D] border-gray-300'}`}
                      onClick={() => setSelectedCycleByProduct(prev => ({ ...prev, [productName]: 'Annual' }))}
                      aria-pressed={cycle === 'Annual' ? 'true' : 'false'}
                    >
                      Annual (50% off)
                    </button>
                  </div>
                  <div className="mt-6 prose prose-sm max-w-none text-[#3D3D3D]/70">
                    <ul className="space-y-3 mb-8">
                      {benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-[#9CAF88] flex-shrink-0 mt-0.5" />
                          <span className="font-sans text-sm text-[#3D3D3D]/80">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <h3 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-2 text-center">{currentTheme?.monthName ?? 'Next month'}'s theme: </h3>
                    <h3 className="text-2xl font-semibold mb-2 text-center" style={{ color: currentTheme?.colorHex ?? undefined }}>
                      {currentTheme?.theme ?? 'TBD'}
                    </h3>
                    <h3 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-2 text-center">{nextTheme?.monthName ?? 'Following month'}'s theme: </h3>
                    <h3 className="text-2xl font-semibold mb-2 text-center" style={{ color: nextTheme?.colorHex ?? undefined }}>
                      {nextTheme?.theme ?? 'TBD'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    className="mt-6 w-full rounded-full bg-[#D4A5A5] hover:bg-[#C49494] px-4 py-3 text-white font-sans"
                    onClick={() => goToSubscribe(productName, plans)}
                  >
                    Subscribe
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Subscriptions;
